import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { GlassCard, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, FileText, Send, Clock, Trash2 } from 'lucide-react';
import { dailyReportService, DailyReport } from '@/lib/storage';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { getLocalDateString } from '@/lib/utils';

const StaffDailyReportPage = () => {
    const { session } = useAuth();
    const [reports, setReports] = useState<DailyReport[]>([]);
    const [todaysReport, setTodaysReport] = useState<DailyReport | null>(null);
    const [reportContent, setReportContent] = useState('');
    const { toast } = useToast();

    useEffect(() => {
        if (session?.userId) {
            loadReports();
            const interval = setInterval(loadReports, 10000);
            return () => clearInterval(interval);
        }
    }, [session]);

    const loadReports = async () => {
        if (!session?.userId) return;

        try {
            const allReports = await dailyReportService.getByStaffId(session.userId);
            setReports(allReports);

            const today = getLocalDateString();
            const todayReport = allReports.find(r => r.date === today);

            if (todayReport) {
                setTodaysReport(todayReport);
                setReportContent(todayReport.content);
            }
        } catch (error) {
            console.error("Failed to load reports", error);
        }
    };

    const handleSubmit = async () => {
        if (!session || !reportContent.trim()) return;

        const today = getLocalDateString();

        try {
            if (todaysReport) {
                await dailyReportService.update(todaysReport.id, reportContent);
                toast({ title: 'Daily report updated' });
            } else {
                await dailyReportService.create({
                    staffId: session.userId,
                    staffName: session.name,
                    date: today,
                    content: reportContent,
                });
                toast({ title: 'Daily report submitted' });
            }

            loadReports();
        } catch (error) {
            toast({ title: 'Failed to submit report', variant: 'destructive' });
        }
    };

    const handleDelete = async (id: string) => {
        if (confirm('Are you sure you want to delete this report?')) {
            try {
                await dailyReportService.delete(id);
                toast({ title: 'Report deleted' });

                if (todaysReport?.id === id) {
                    setTodaysReport(null);
                    setReportContent('');
                }

                loadReports();
            } catch (error) {
                toast({ title: 'Failed to delete report', variant: 'destructive' });
            }
        }
    };

    // Animations
    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: { staggerChildren: 0.05 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 350, damping: 25 } }
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="space-y-6"
        >
            {/* Header */}
            <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Daily Reports</h2>
                <p className="text-sm text-slate-500 font-medium">Draft and submit your daily campaign execution summaries.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Today's Report Form */}
                <motion.div variants={itemVariants} className="lg:col-span-6 space-y-4">
                    <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl shadow-sm bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
                        <CardHeader className="pb-3 pt-6 px-6 border-b border-slate-100 dark:border-slate-800/40">
                            <CardTitle className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                <FileText className="w-5 h-5 text-indigo-500" />
                                Today's Workspace Summary
                                <span className="text-xs font-semibold text-slate-450 dark:text-slate-500 ml-auto">
                                    {new Date().toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                                </span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-6 space-y-4">
                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">
                                    Report details
                                </label>
                                <Textarea
                                    placeholder="Briefly summarize your daily outputs:&#10;1. Audited field campaign platforms&#10;2. Verified task priorities with team&#10;3. Executed pending assignments"
                                    value={reportContent}
                                    onChange={(e) => setReportContent(e.target.value)}
                                    rows={10}
                                    className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-1 focus:ring-slate-350 dark:focus:ring-slate-750 resize-none text-sm leading-relaxed font-semibold p-4"
                                />
                            </div>

                            <div className="flex justify-end pt-2">
                                <Button
                                    variant="royal"
                                    onClick={handleSubmit}
                                    disabled={!reportContent.trim()}
                                    className="rounded-xl font-bold gap-2 px-5 h-11 shadow-md shadow-primary/5"
                                >
                                    <Send className="w-4 h-4" />
                                    {todaysReport ? 'Update Summary' : 'Submit Summary'}
                                </Button>
                            </div>
                        </CardContent>
                    </GlassCard>
                </motion.div>

                {/* Previous Reports History */}
                <motion.div variants={itemVariants} className="lg:col-span-6 space-y-4">
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                        <Clock className="w-5 h-5 text-indigo-500" />
                        Submission Logs ({reports.length})
                    </h3>

                    <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 scrollbar-thin">
                        {reports.length === 0 ? (
                            <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl shadow-sm bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
                                <CardContent className="py-12 text-center text-slate-400 font-medium">
                                    No reports submitted yet. Submit your first summary today!
                                </CardContent>
                            </GlassCard>
                        ) : (
                            <AnimatePresence mode="popLayout">
                                {reports.map((report) => (
                                    <motion.div
                                        key={report.id}
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ type: "spring", stiffness: 350, damping: 25 }}
                                        className="h-full"
                                    >
                                        <GlassCard className="relative group border-slate-200/50 dark:border-slate-850 shadow-sm rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl overflow-hidden">
                                            <CardContent className="p-5 space-y-3.5">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2 text-indigo-650 dark:text-indigo-400 font-bold text-xs">
                                                        <Calendar className="w-4 h-4 text-indigo-500" />
                                                        {new Date(report.date + 'T00:00:00').toLocaleDateString(undefined, {
                                                            weekday: 'short',
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric',
                                                        })}
                                                    </div>
                                                    <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide">
                                                        {new Date(report.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                </div>

                                                <div className="bg-slate-100/50 dark:bg-slate-950/40 border border-slate-100 dark:border-slate-850 rounded-2xl p-4 text-xs font-semibold leading-relaxed text-slate-700 dark:text-slate-350 whitespace-pre-wrap">
                                                    {report.content}
                                                </div>

                                                <div className="absolute top-4.5 right-4.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/20"
                                                        onClick={() => handleDelete(report.id)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </CardContent>
                                        </GlassCard>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        )}
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default StaffDailyReportPage;
